// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.

/** Profile photos: shrunk and cropped here, then uploaded (PUT /v1/me/avatar). */
import type { components } from "@granite/api-client";

import { accessToken, apiBaseUrl, sessionStore } from "./session.js";

type User = components["schemas"]["User"];

/** The size the apps upload: a 256 x 256 square. */
const SIDE = 256;

/** A photo's address in this app (the API gives a path on the API server). */
export function avatarSrc(path: string | undefined): string | undefined {
  return path ? `${apiBaseUrl}${path}` : undefined;
}

/** The middle square of the image, at 256 x 256, as a JPEG. */
export async function squareJpeg(file: Blob): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  const side = Math.min(bitmap.width, bitmap.height);
  const canvas = document.createElement("canvas");
  canvas.width = SIDE;
  canvas.height = SIDE;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("The image could not be read.");
  // Transparent images get a white background (JPEG has no transparency).
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, SIDE, SIDE);
  ctx.drawImage(bitmap, (bitmap.width - side) / 2, (bitmap.height - side) / 2, side, side, 0, 0, SIDE, SIDE);
  bitmap.close();
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => {
        if (b) resolve(b);
        else reject(new Error("The image could not be read."));
      },
      "image/jpeg",
      0.9,
    );
  });
}

/** Uploads the photo (cropped and shrunk) and returns the account with it. */
export async function uploadAvatar(file: Blob): Promise<User> {
  const body = await squareJpeg(file);
  const token = await accessToken();
  const res = await fetch(`${apiBaseUrl}/v1/me/avatar`, {
    method: "PUT",
    headers: { "Content-Type": "application/octet-stream", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body,
  });
  if (!res.ok) {
    const problem = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(problem.detail ?? `The upload failed (${String(res.status)}).`);
  }
  const user = (await res.json()) as User;
  rememberUser(user);
  return user;
}

/** Keeps the signed-in user (the account menu) in step with a changed account. */
export function rememberUser(user: User): void {
  sessionStore.setState((s) => (s.status === "signedIn" || s.status === "offline" ? { ...s, user } : s));
}
